import {
  Grid,
  GridItem,
  InputLeftElement,
  InputRightElement,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React from "react";
import useGetUserRole from "../../hooks/useGetUserRole";
import {
  rateBuyDownSelects,
  selectPrepayPenalty,
} from "../../lib/models/rental/rental-form-selects.model";
import { RentalForm } from "../../lib/models/rental/rental-form.model";
import {
  getPremierPrepayPenaltySelects,
  selectOpenPrepay,
} from "../../lib/models/rental/rental-premier-form-selects.model";
import { QuoteType } from "../../lib/types/QuoteType";
import { isInternalUser } from "../../lib/UserPermissions";
import FormSectionHeader from "../common-quote/FormSectionHeader";
import CheckboxField from "../form-fields/CheckboxField";
import SelectField from "../form-fields/SelectField";
import SliderField from "../form-fields/SliderField";
import TextInputField from "../form-fields/TextInputField";
import ShowForRole from "../ShowForRole";

function RentalPricingForm({ quoteType }: { quoteType: QuoteType }) {
  const role = useGetUserRole();

  const { values, setFieldValue, setFieldTouched, validateForm } =
    useFormikContext<RentalForm>();

  const { productType, submissionType, prepaymentPenalty } = values;

  React.useEffect(() => {
    if (quoteType === "Rental") {
      if (!selectPrepayPenalty.find((x) => x.value === prepaymentPenalty)) {
        setFieldValue("prepaymentPenalty", selectPrepayPenalty[3].value);
      }
    } else if (quoteType === "Premier") {
      const prepayOptions = getPremierPrepayPenaltySelects(productType);
      setFieldValue("prepaymentPenalty", prepayOptions[0].value);
    }

    Promise.resolve(validateForm())
      .then(() => {})
      .catch((error) => console.error(error));
  }, [quoteType, productType, setFieldValue, validateForm, prepaymentPenalty]);

  React.useEffect(() => {
    if (prepaymentPenalty !== "") {
      setFieldTouched("prepaymentPenalty", true, true);
    }
  }, [prepaymentPenalty, setFieldTouched]);

  return (
    <Grid
      templateColumns="repeat(12, minmax(0, 1fr))"
      gap={{ base: 6, md: 8, lg: 8 }}
    >
      <GridItem colSpan={12}>
        <FormSectionHeader id="pricing" label="Pricing" />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SliderField
          label="Yield Spread Premium"
          name="yieldSpread"
          defaultValue={0.0}
          step={0.125}
          max={1.0}
          min={0.0}
          precision={3}
        />
      </GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}></GridItem>

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SliderField
          label="Origination Fee"
          name="originationFeeAdjustment"
          defaultValue={1.5}
          step={0.125}
          max={2.0}
          min={1.0}
          precision={3}
          inputFieldDisable={true}
        />
      </GridItem>

       <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SliderField
          label="Interest Rate Buy Down (+) / Buy Up (-) Fee"
          name="buyUpDownPercentage"
          defaultValue={0}
          step={0.125}
          max={4}
          min={-2}
          precision={3}
          inputFieldDisable={true}
          
        />
      </GridItem>

      <ShowForRole
        roles={["Wholesale", "Wholesale Manager", "Capital Markets"]}
      >
        <>
          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <SelectField
              label="Rate Buy Down Fee"
              name="interestRateAddOn"
              placeholder="Select Rate Buy Down Fee"
              data={rateBuyDownSelects}
            />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <TextInputField
              label="Fee Adjustment"
              name="processingFee"
              type="number"
              formatNumber
              children={<InputLeftElement children="$" />}
            />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <TextInputField
              label="Partner Origination Fee"
              name="originationPointsAddOn"
              type="number"
              formatNumber
              children={<InputRightElement children="%" />}
            />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}></GridItem>
        </>
      </ShowForRole>

      {quoteType === "Premier" ?? (
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <SelectField
            label="Open Prepay Option"
            name="openPrepayOption"
            placeholder="Select Open Prepay Option"
            data={selectOpenPrepay}
          />
        </GridItem>
      )}

      {submissionType.includes("Broker") && (
        <>
          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <TextInputField
              label="Broker Fee"
              type="number"
              name="brokerFee"
              children={<InputRightElement children="%" />}
            />
          </GridItem>

          {isInternalUser(role) && (
            <GridItem colSpan={{ base: 12, lg: 6 }}>
              <TextInputField
                label="Broker Processing Fee"
                name="brokerProcessingFee"
                type="number"
                formatNumber
                children={<InputLeftElement children="$" />}
              />
            </GridItem>
          )}
        </>
      )}

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <SelectField
          label="Prepayment Penalty"
          name="prepaymentPenalty"
          placeholder="Use Default Prepayment Penalty"
          data={selectPrepayPenalty}
        />
      </GridItem>
      {isInternalUser(role) && (
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <TextInputField
            label="Valuation Fee"
            type="number"
            name="appraisalFee"
            children={<InputLeftElement children="$" />}
          />
        </GridItem>
      )}

      <GridItem colSpan={{ base: 12, lg: 6 }}>
        <CheckboxField
          label="Refinancing L1C FixNFlip Loan? (OPTIONAL)"
          name="refiL1cFixNFlip"
        />
      </GridItem>

      {submissionType === "Broker" && (
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <CheckboxField
            label="Zero Percent Lender Fee?"
            name="isZeroOrigFee"
          />
        </GridItem>
      )}

      {quoteType === "Rental" ? (
        <>
          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField
              label="Are there financed properties less than $150,000?"
              name="propertyVal"
            />
          </GridItem>

          {isInternalUser(role) && (
            <GridItem colSpan={{ base: 12, lg: 6 }}>
              <CheckboxField label="Paid Referral?" name="borrowerIsReferral" />
            </GridItem>
          )}

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField label="Seasonal Rental?" name="seasonalRental" />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField label="5-9 Unit?" name="fiveNineUnit" />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField label="Condo?" name="condo" />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField label="2-4 Unit?" name="twoFourUnit" />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField label="Vacant Property?" name="vacantProperty" />
          </GridItem>
        </>
      ) : (
        <>
          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField
              label="Is 25% of the collateral less than $75,000?"
              name="collateral"
            />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField
              label="Is the borrower a referral?"
              name="referral"
            />
          </GridItem>

          <GridItem colSpan={{ base: 12, lg: 6 }}>
            <CheckboxField
              label="Are condos 25% of the collateral?"
              name="collateralCondos"
            />
          </GridItem>
        </>
      )}
    </Grid>
  );
}

export default RentalPricingForm;
